/* global globalThis */
import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { useParams, useLocation } from "react-router-dom";
import { addNoteApi, editNoteApi, fetchNotes } from "../../api/notes";
import {
  Box,
  Typography,
  Stack,
  Button,
  Chip,
  Container,
  styled,
  Grid,
  Tabs,
  Tab,
  Snackbar,
  Alert,
} from "@mui/material";
import NotesCard from "../cards/NotesCard";
import Breadcrumb from "../common/Breadcrumb";
import AddNoteModal from "../modals/AddNoteModal";
import OutreachDispositionModal from "../modals/ProviderDisposition";
import AddIcon from "@mui/icons-material/Add";
import {
  fetchPharmacyOpportunities,
  fetchProviderOpportunities,
  fetchPharmacyOverview,
} from "../../api/provider";
import ContactPharmacyInfo from "./contactPharmacyInfo";
import PharmacyMember from "./PharmacyMember";
import { useLoading } from "../../context/LoadingContext";
import MemberQuickview from "../members/MemberQuickview";

function buildProgramCategoryMap(mappedResults) {
  const programCategoryMap = {};
  for (const opp of mappedResults) {
    const category = opp.programs?.[0]?.name || "General";
    if (!programCategoryMap[category]) {
      programCategoryMap[category] = {
        members: [],
        outreachReasons: [],
      };
    }
    if (
      opp.member?.memberId &&
      !programCategoryMap[category].members.some(
        (m) => m.id === opp.member.memberId,
      )
    ) {
      programCategoryMap[category].members.push({
        id: opp.member.memberId,
        name: opp.member.name,
        manualTaskId: opp.manualTaskId,
      });
    }
    if (
      opp.outreachReason &&
      !programCategoryMap[category].outreachReasons.includes(opp.outreachReason)
    ) {
      programCategoryMap[category].outreachReasons.push(opp.outreachReason);
    }
  }
  return programCategoryMap;
}

function getScreenInches() {
  const { width, height } = globalThis.screen;
  const dpi = globalThis.devicePixelRatio * 96;
  const widthInInches = width / dpi;
  const heightInInches = height / dpi;
  return Math.hypot(widthInInches, heightInInches);
}

const ContentContainer = styled("div")(() => ({
  flexGrow: 1,
  width: "100%",
  backgroundColor: "#F7F8FB",
  display: "flex",
  flexDirection: "column",
  overflowY: "auto",
  overflowX: "hidden",
  height: "calc(100vh - 64px)",
  paddingTop: "10px",
}));

function mapApiResultsToTable(data) {
  return (data.content || []).map((item, idx) => ({
    member: {
      memberId: item.member?.id?.toString() || "",
      name: item.member?.fullName
        ? item.member.fullName.charAt(0).toUpperCase() +
          item.member.fullName.slice(1).toLowerCase()
        : "",
      language: item.member?.language || "English",
      timeZone: item.member?.timezone || "EST",
      gender: "",
      age: item.member?.dob
        ? new Date().getFullYear() - new Date(item.member.dob).getFullYear()
        : "",
      dob: item.member?.dob || "",
      knownAllergies: item.member?.allergy ? [item.member.allergy] : [],
      contact: {
        phone: item.member?.phoneNumber || "",
        email: item.member?.email || "",
      },
      address: "",
    },
    memberBadge: "",
    type: item.outreachReason || "",
    provider: {
      name: item?.provider?.fullName || "",
      contact: item?.provider?.phone || "",
    },
    pharmacy: {
      name: item.pharmacy?.name || "",
      contact: item.pharmacy?.phone || "",
    },
    programs:
      item.memberPrograms?.map((p) => ({
        name: p.name || "",
        status: p.status || "",
      })) || [],
    recentActivity: {
      status: item.recentActivity?.outcome ?? item.status ?? "N/A",
      role: item.recentActivity?.role ?? item.recentActivityRole ?? "",
      timestamp: item.recentActivity?.timestamp ?? item.recentActivityAt ?? "",
    },
    status: item.recentActivity?.status || "N/A",
    dueDate: item.dueDate || "",
    id: idx + 1,
    outreachReason: item.outreachReason || "",
    manualTask: {
      id: item.manualTask?.id || "",
      noOfAttempts: item.manualTask?.attemptCount || 0,
      taskNote: item.manualTask?.note || "",
      outreachReason: item.manualTask?.outreachReason || "",
      dueDate: item.manualTask?.dueDate || "",
    },
  }));
}

async function fetchOpportunitiesData({
  pharmacyId,
  providerId,
  setProgramMap,
  props,
  showLoading,
  hideLoading,
}) {
  showLoading?.();
  try {
    let data = {};
    if (props.pharmacyId) {
      data = await fetchPharmacyOpportunities(pharmacyId, "");
    } else if (props.providerId) {
      data = await fetchProviderOpportunities(providerId, "");
    } else {
      hideLoading?.();
      return;
    }
    const progMap = {};
    for (const item of data.content) {
      if (item.program?.programId && item.program?.programName) {
        progMap[item.program.programId] = item.program.programName;
      }
    }
    setProgramMap(progMap);

    const mappedResults = mapApiResultsToTable(data);
    buildProgramCategoryMap(mappedResults);
  } catch (error) {
    console.error("Unexpected error", error);
  } finally {
    hideLoading?.();
  }
}

const MemberDetailsPharmacy = (props) => {
  const location = useLocation();
  const fromMemberMonitoring = location.state?.fromMemberMonitoring;
  const { memberId } = useParams();
  const { showLoading, hideLoading } = useLoading();
  const [selectedTab, setSelectedTab] = useState("Overview");
  const [dispositionOpportunity, setDispositionOpportunity] = useState(null);
  const [selectedNoteType, setSelectedNoteType] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTimestamp, setSearchTimestamp] = useState(Date.now());
  const [containerScale, setContainerScale] = useState(1);
  const [memberOptions, setMemberOptions] = useState([]);
  const [memberDrugMap, setMemberDrugMap] = useState({});
  const [programMap, setProgramMap] = useState({});
  const [opportunitiesReloadKey, setOpportunitiesReloadKey] = useState(0);
  const [quickViewMember, setQuickViewMember] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);

  const deleteSelectedRef = useRef(null);
  const [selectedOpportunityCount, setSelectedOpportunityCount] = useState(0);

  const handleQuickView = useCallback((member) => {
    if (!member) return;
    const memberId = member?.member?.memberId || member?.memberId || member?.id;
    if (!memberId) return;

    const rawBadge = member?.memberBadge || member?.member?.memberBadge || "";
    const badgeLower = rawBadge.toLowerCase();

    const quickViewSource =
      badgeLower.includes("member monitoring") ||
      badgeLower.includes("graduation completed")
        ? "memberMonitoring"
        : "priorityOutreach";
    const notes = localStorage.getItem(`member_${memberId}_notes`);
    setSelectedMember({
      ...member,
      memberBadge: rawBadge,
      navigationSource: {
        badgeType: rawBadge,
        fromMemberMonitoring: quickViewSource === "memberMonitoring",
        fromPriorityOutreach: quickViewSource === "priorityOutreach",
        source:
          quickViewSource === "memberMonitoring"
            ? "monitoring"
            : "priority-outreach",
      },
      notes: notes ? JSON.parse(notes) : [],
    });
    setQuickViewMember(memberId);
  }, []);

  const handleCloseQuickView = useCallback(() => {
    setQuickViewMember(null);
    setSelectedMember(null);
  }, []);

  const pharmacyId =
    props.pharmacyId || props.selectedMemberId || props.memberId || props.id;
  const providerId =
    props.providerId || props.selectedMemberId || props.memberId || props.id;

  useEffect(() => {
    if (props.pharmacyId || props.providerId) {
      void fetchOpportunitiesData({
        pharmacyId,
        providerId,
        setProgramMap,
        props,
        showLoading,
        hideLoading,
      });
    }
  }, [
    pharmacyId,
    providerId,
    props,
    props.pharmacyId,
    props.providerId,
    showLoading,
    hideLoading,
  ]);

  const fetchMemberData = useCallback(async () => {
    if (!memberId) return;

    try {
      const data = await fetchPharmacyOverview(memberId);

      setMemberData((prev) => ({
        ...prev,
        ...data,
      }));
    } catch (error) {
      console.error("Unexpected error", error);
    } finally {
      console.log("In finally block");
    }
  }, [memberId]);

  const fetchMemberNotes = useCallback(async () => {
    if (!memberId) return;

    try {
      setNotesLoading(true);

      const queryParams = { noteType: selectedNoteType };
      const isArchivedSelected =
        Array.isArray(selectedNoteType) &&
        selectedNoteType.map((t) => t.toLowerCase()).includes("archived");
      if (
        !isArchivedSelected &&
        selectedNoteType &&
        selectedNoteType.length > 0
      ) {
        queryParams.noteType = selectedNoteType;
      }
      if (searchQuery && searchQuery.trim() !== "") {
        queryParams.searchQuery = searchQuery.trim();
      }

      const response = await fetchNotes(entityType, memberId, queryParams);

      const notesList = (response.content || []).filter((note) => {
        const hasContent = note.content && note.content.trim() !== "";

        const hasNoteEntry = note.noteEntry && note.noteEntry.trim() !== "";
        return hasContent || hasNoteEntry;
      });

      const formattedNotes = notesList.map((note) => ({
        id: note.id,
        type: note.type,
        content: note.content || note.noteEntry || "",
        details: note.content || note.noteEntry || "",
        author: note.createdBy?.toString() || "1",
        pinNote: note.pinned,
        rating: note.rating,
        programName: note.programName,
        createdAt: note.createdAt,
        updateAt: note.updateAt || note.updatedAt,
        date: new Date(note.createdAt).toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }),
        status: note.status,
      }));
      setNotes(formattedNotes);
    } catch (error) {
      console.error("Unexpected error", error);
      setNotes([]);
    } finally {
      setNotesLoading(false);
    }
  }, [memberId, searchQuery, selectedNoteType]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchMemberNotes();
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, selectedNoteType, fetchMemberNotes]);

  useEffect(() => {
    if (memberId) {
      fetchMemberData();
      fetchMemberNotes();
    }
  }, [memberId, fetchMemberData, fetchMemberNotes]);
  const [memberData, setMemberData] = useState({
    name: "",
    memberId: "",
    personCode: "",
    status: "Active",
    sex: "",
    gender: "",
    dateOfBirth: "",
    email: "",
    timeZone: "",
    allergies: [],
    street: "",
    state: "",
  });
  const [notes, setNotes] = useState([]);

  const [notesLoading, setNotesLoading] = useState(false);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isDispositionModalOpen, setIsDispositionModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [dispositionTitle, setDispositionTitle] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const entityType = "PHARMACY";

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };
  useEffect(() => {
    const fetchMemberData = async () => {
      try {
        showLoading();
        const data = await fetchPharmacyOverview(memberId);

        const address =
          data && typeof data === "object" ? data.address || {} : {};

        if (!data || typeof data !== "object") {
          setMemberData((prevData) => ({ ...prevData, loading: false }));
          return;
        }

        const address2 =
          typeof address === "object" ? address.address2 || "" : "";
        const city = typeof address === "object" ? address.city || "" : "";
        const state = typeof address === "object" ? address.state || "" : "";
        const zip = typeof address === "object" ? address.zip || "" : "";
        const fullAddress =
          typeof address === "object" ? address.fullAddress || "" : "";

        setMemberData((prevData) => ({
          ...prevData,
          name: data.name || "",
          memberId: data.memberId || "",
          nabp: data.nabp || "N/A",

          status: data.status || "Active",
          email: data.email || "",
          phone: data.phone || "",
          faxNumber: data.faxNumber || "",

          address2: address2,
          city: city,
          state: state,
          zip: zip,

          contact: {
            phone: data.phone || "",
            email: data.email || "",
            fax: data.faxNumber || "",
            address: fullAddress || "",
            language: "N/A",
            timeZone: "N/A",
          },

          location2: data.location2 || {},

          language: data.language || "N/A",
          timeZone: data.timeZone || "N/A",
        }));
      } catch (e) {
        console.error("Error fetching member data:", e);
        hideLoading();
      } finally {
        hideLoading();
      }
    };

    const fetchMemberNotes = async () => {
      if (!memberId) return;

      try {
        setNotesLoading(true);

        const queryParams = {
          noteType: selectedNoteType,
          searchQuery: searchQuery || "",
        };

        const response = await fetchNotes(entityType, memberId, queryParams);

        const notesList = (response.content || []).filter((note) => {
          const hasContent = note.content && note.content.trim() !== "";
          const hasNoteEntry = note.noteEntry && note.noteEntry.trim() !== "";
          return hasContent || hasNoteEntry;
        });

        const formattedNotes = notesList.map((note) => {
          return {
            ...note,
            id: note.id,
            type: note.type,
            content: note.content || note.noteEntry || "",
            details: note.content || note.noteEntry || "",
            author: note.createdBy?.toString() || "1",
            pinNote: note.pinned,
            rating: note.rating,
            programName: note.programName,

            createdAt: note.createdAt,
            updateAt: note.updateAt || note.updatedAt || null,
            updatedAt: note.updateAt || note.updatedAt || null,
          };
        });

        setNotes(formattedNotes);
      } catch (error) {
        console.error("Unexpected error", error);
        setNotes([]);
      } finally {
        setNotesLoading(false);
      }
    };

    if (memberId) {
      void fetchMemberData();
      void fetchMemberNotes();
    }
  }, [
    memberId,
    searchQuery,
    selectedNoteType,
    searchTimestamp,
    showLoading,
    hideLoading,
  ]);

  const handleSaveContactInfo = (updatedInfo) => {
    setMemberData((prev) => {
      const newLocation2 = updatedInfo.location2
        ? {
            ...prev.location2,
            ...updatedInfo.location2,
          }
        : prev.location2;

      return {
        ...prev,

        phone: updatedInfo.phoneNumber || prev.phone,
        preferredPhoneNum:
          updatedInfo.preferredPhoneNumber || prev.preferredPhoneNum,
        preferredPhoneNumber:
          updatedInfo.preferredPhoneNumber || prev.preferredPhoneNumber,
        email: updatedInfo.email || prev.email,

        contact: {
          ...prev.contact,
          phone: updatedInfo.phoneNumber || prev.contact?.phone,
          preferredPhoneNumber:
            updatedInfo.preferredPhoneNumber ||
            prev.contact?.preferredPhoneNumber,
          email: updatedInfo.email || prev.contact?.email,
          language: updatedInfo.language || prev.contact?.language,
          timeZone: updatedInfo.timeZone || prev.contact?.timeZone,
          address: updatedInfo.address || prev.contact?.address,
        },

        location2: newLocation2,
      };
    });
  };

  const handleAddDisposition = async (newDisposition) => {
    try {
      setMemberData((prev) => ({
        ...prev,
        dispositions: [
          {
            ...newDisposition,
            id: Date.now().toString(),
            author: "Current User",
            date: new Date()
              .toLocaleString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              })
              .replace(",", ""),
          },
          ...(prev.dispositions || []),
        ],
      }));

      setIsDispositionModalOpen(false);

      setOpportunitiesReloadKey((prev) => prev + 1);

      await fetchMemberNotes();
      await fetchMemberData();

      setDispositionOpportunity(null);

      setSnackbar({
        open: true,
        message: "Disposition added successfully",
        severity: "success",
      });
    } catch {
      setSnackbar({
        open: true,
        message: "Failed to add disposition. Please try again.",
        severity: "error",
      });
    }
  };

  const toggleNotePin = async (noteId) => {
    try {
      setNotesLoading(true);

      const noteToToggle = notes.find((note) => note.id === noteId);
      if (!noteToToggle) return;

      if (
        noteToToggle.status === "ARCHIVED" ||
        noteToToggle.status === "CREATED"
      ) {
        const newStatus =
          noteToToggle.status === "ARCHIVED" ? "CREATED" : "ARCHIVED";
        await editNoteApi(noteToToggle.id, {
          ...noteToToggle,
          status: newStatus,
        });
        await fetchMemberNotes();
      }

      if (noteToToggle.pinNote) {
        await editNoteApi(noteId, {
          ...noteToToggle,
          pinned: false,
        });
      } else {
        const currentlyPinnedNote = notes.find((note) => note.pinNote);

        if (currentlyPinnedNote) {
          await editNoteApi(currentlyPinnedNote.id, {
            ...currentlyPinnedNote,
            pinned: false,
          });
        }

        await editNoteApi(noteId, {
          ...noteToToggle,
          pinned: true,
        });
      }

      await fetchMemberNotes();

      setSnackbar({
        open: true,
        message: noteToToggle.pinNote ? "Note unpinned" : "Note pinned",
        severity: "success",
      });
    } catch {
      setSnackbar({
        open: true,
        message: "Failed to update pin status. Please try again.",
        severity: "error",
      });
    } finally {
      setNotesLoading(false);
    }
  };

  const handleEditNote = async (note) => {
    setEditingNote(note);
    setIsNoteModalOpen(true);
  };

  const handleAddNote = async (newNote) => {
    try {
      setNotesLoading(true);
      showLoading?.();
      const isEdit = !!editingNote;

      if (newNote.pinNote && !isEdit) {
        const hasPinnedNote = notes.some((note) => note.pinNote);
        if (hasPinnedNote) {
          return "Please unpin the current pinned note before pinning a new one";
        }
      }

      const payload = {
        type: newNote.noteTypes?.[0] || "General",
        content: newNote.noteDetails || "",
        rating: newNote.rating || 1,
        pinned: newNote.pinNote || false,
        programId: Number(newNote.programId),
        entityType: entityType,
        status: newNote.status || (newNote.archived ? "ARCHIVED" : "CREATED"),
        archived: newNote.archived === undefined ? false : newNote.archived,
      };

      if (isEdit) {
        await editNoteApi(editingNote.id, payload);
      } else {
        await addNoteApi(memberId, {
          ...payload,
          entityType: entityType,
          entityId: Number.parseInt(memberId, 10),
          date: new Date().toISOString(),
        });
      }

      await fetchMemberNotes();

      setIsNoteModalOpen(false);
      setEditingNote(null);
    } catch (error) {
      console.error("Error saving note:", error);
      setSnackbar({
        open: true,
        message: `Failed to ${
          editingNote ? "update" : "add"
        } note. Please try again.`,
        severity: "error",
      });
      setIsNoteModalOpen(false);
      setEditingNote(null);
    } finally {
      setNotesLoading(false);
      hideLoading?.();
    }
  };

  const getBreadcrumbItems = () => {
    return [
      { label: "Pharmacy Outreach", path: "/pharmacy-outreach" },
      { label: "Pharmacy Profile", path: "" },
    ];
  };

  useEffect(() => {
    const inches = getScreenInches();
    if (inches < 14) {
      setContainerScale(0.9);
    } else {
      setContainerScale(1);
    }
  }, []);
  return (
    <React.Fragment>
      <div
        className="member-details-page"
        style={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#F7F8FB",
          overflow: "hidden",
        }}
      >
        <ContentContainer
          sx={{
            flex: 1,
            transform: `scale(${containerScale})`,
            transformOrigin: "top left",
            width: containerScale < 1 ? "111%" : "100%",
            position: "relative",
            zIndex: 1,
            overflow: "hidden",
          }}
        >
          <Container
            maxWidth={false}
            disableGutters
            sx={{
              padding: "0 12px",
              height: "100%",
              backgroundColor: "#F7F8FB",
              overflow: "auto",
              overflowX: "hidden",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#555",
              },
            }}
          >
            <Breadcrumb items={getBreadcrumbItems()} />

            <Box
              sx={{
                backgroundColor: "#ffffff",
                borderRadius: "12px",
                padding: "16px 24px",
                marginTop: "24px",
                marginBottom: "16px",
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
              >
                <Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap="8px"
                    flexWrap="wrap"
                  >
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      fontSize={"18px"}
                      sx={{ color: "#403C6F" }}
                    >
                      {memberData.name}
                    </Typography>
                    <Chip
                      label={memberData.status}
                      color="success"
                      size="small"
                      sx={{
                        fontWeight: 600,
                        backgroundColor: "#e8f5e9",
                        color: "#2e7d32",
                        border: "1px solid #45B184",
                        height: "24px",
                        "& .MuiChip-label": {
                          padding: "0 8px",
                          fontSize: "0.75rem",
                        },
                      }}
                    />
                    {memberData.doNotCall && (
                      <Chip
                        label="Do Not Call"
                        color="error"
                        size="small"
                        sx={{
                          fontWeight: 600,
                          backgroundColor: "#FFF3F3",
                          color: "#E77877",
                          border: "1px solid #FEADAC",
                          height: "24px",
                          "& .MuiChip-label": {
                            padding: "0 8px",
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                    )}
                  </Box>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    mt={1}
                    flexWrap="wrap"
                    rowGap={1}
                  >
                    <Typography variant="body2" sx={{ color: "#7F7C9F" }}>
                      NABP:{" "}
                      <span style={{ color: "#403C6F", fontWeight: 600 }}>
                        {memberData.nabp}
                      </span>
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={2}
              flexWrap="wrap"
              rowGap={2}
            >
              <Box
                sx={{
                  backgroundColor: "#ffffff",
                  borderRadius: "20px",
                  padding: "8px 4px 0",
                  marginBottom: "1px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  marginRight: 0,
                }}
              >
                <Tabs
                  value={selectedTab}
                  onChange={(e, newValue) => setSelectedTab(newValue)}
                  sx={{
                    minHeight: 36,
                    "& .MuiTabs-flexContainer": {
                      gap: 1,
                    },
                    "& .MuiTab-root": {
                      minHeight: 30,
                      padding: "6px 8px",
                      fontSize: "14px",
                      fontWeight: 500,
                      fontStyle: "semiBold",
                      textTransform: "none",
                      minWidth: "auto",
                      position: "relative",
                      color: "#4b3f72",
                      "&:hover": {
                        color: "#4b3f72",
                      },
                      "&.Mui-selected": {
                        color: "#fff",
                        fontWeight: 600,
                        backgroundColor: "#4b3f72",
                        padding: "4px 12px",
                        borderRadius: "20px",
                      },
                    },
                    "& .MuiTabs-indicator": {
                      backgroundColor: "#fff",
                      height: "3px",
                      borderRadius: "3px 3px 0 0",
                    },
                  }}
                >
                  {["Overview", "Opportunities"].map((tab) => (
                    <Tab
                      key={tab}
                      label={tab}
                      value={tab}
                      sx={{
                        minWidth: "auto",
                        padding: "6px 16px",
                        "&.Mui-selected": {
                          color: "#4b3f72",
                          fontWeight: 600,
                        },
                      }}
                    />
                  ))}
                </Tabs>
              </Box>
              <Box sx={{ display: "flex", gap: 1, mr: 1, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={
                    location?.state?.showDisposition === false ||
                    !dispositionOpportunity
                  }
                  sx={{
                    color: "#403C6F",
                    border: "none !important",

                    textTransform: "none",
                    borderRadius: 20,
                    fontWeight: 500,
                    px: 2,
                    backgroundColor:
                      location?.state?.showDisposition === false ||
                      !dispositionOpportunity
                        ? "#EFEFEF"
                        : "#ffffff",
                    "&:hover": {
                      backgroundColor: "rgba(75, 63, 114, 0.04)",
                      borderColor: "#403C6F",
                    },
                  }}
                  onClick={() => {
                    setIsDispositionModalOpen(true);
                    setDispositionTitle("Add Disposition");
                  }}
                >
                  Add Disposition
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<AddIcon />}
                  onClick={() => setIsNoteModalOpen(true)}
                  sx={{
                    color: "#403C6F",
                    border: "none",

                    borderRadius: 20,
                    fontWeight: 500,
                    background: "#ffffff",
                    pt: 0.7,
                    pb: 0.7,
                    px: 2,
                    "&:hover": {
                      backgroundColor: "rgba(75, 63, 114, 0.04)",
                      borderColor: "#403C6F",
                    },
                  }}
                >
                  Add Notes
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  disabled={
                    selectedTab !== "Opportunities" ||
                    selectedOpportunityCount === 0
                  }
                  onClick={() => {
                    if (deleteSelectedRef.current) deleteSelectedRef.current();
                  }}
                  sx={{
                    minWidth: "120px",
                    height: "30px",
                    backgroundColor: "transparent",
                    color: "#F15B59",
                    border: "1px solid #F15B59",
                    textTransform: "none",
                    borderRadius: "20px",
                    fontSize: "13px",
                    fontWeight: 500,

                    boxShadow: "none",
                    "&.Mui-disabled": {
                      background:
                        selectedTab !== "Opportunities" ||
                        selectedOpportunityCount === 0
                          ? "#EFEFEF !important"
                          : "#ffffff",
                      color: "#999893 !important",
                      border: "none",
                      boxShadow: "none",
                    },
                    "&:hover": {
                      backgroundColor: "transparent",
                      borderColor: "#E53935",
                      color: "#E53935",
                      boxShadow: "none",
                    },
                  }}
                >
                  Delete Task
                </Button>
              </Box>
            </Box>

            {selectedTab === "Overview" && (
              <Box
                sx={{
                  display: "flex",
                  mt: 2,
                  alignItems: { xs: "stretch", md: "flex-start" },
                  gap: { xs: 1, sm: 2, md: 2 },
                  flexDirection: { xs: "column", md: "row" },
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: 1, sm: 2, md: 2 },
                  }}
                >
                  <ContactPharmacyInfo
                    contactInfo={memberData}
                    onSave={handleSaveContactInfo}
                    pharmacyId={memberId}
                  />
                </Box>

                <Box
                  sx={{
                    flex: 2,
                    minWidth: { xs: "100%", md: 450 },
                    maxWidth: 700,
                    position: "sticky",
                    top: 20,
                    height: "fit-content",
                    mt: { xs: 2, md: 0 },
                    "& > div": {
                      width: "100%",
                      height: "100%",
                      maxWidth: "100%",
                      boxShadow: "0px 2px 6px rgba(0,0,0,0.05)",
                      borderRadius: "8px",
                      border: "1px solid #E0E0E0",
                      overflow: "hidden",
                    },
                  }}
                >
                  <NotesCard
                    notes={notes}
                    title="Notes"
                    selectedNoteType={selectedNoteType}
                    searchQuery={searchQuery}
                    inQuickview={false}
                    onSearch={(query) => {
                      setSearchQuery(query || "");
                      setSearchTimestamp(Date.now());
                    }}
                    onNoteTypeChange={(types) => {
                      setSelectedNoteType(
                        Array.isArray(types) ? types : [types],
                      );
                    }}
                    onTogglePin={toggleNotePin}
                    onEdit={handleEditNote}
                    loading={notesLoading}
                    onAddNote={() => setIsNoteModalOpen(true)}
                    containerHeight="704px"
                    sx={{
                      "& .MuiBox-root": {
                        mb: 0,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      },
                      "& .MuiCardContent-root": {
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        padding: 0,
                      },
                      "& .notes-list-container": {
                        flex: 1,
                        overflow: "hidden",
                        "& > div": {
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                        },
                      },
                    }}
                  />
                </Box>
              </Box>
            )}

            {selectedTab === "Opportunities" && (
              <Grid item xs={12} md={12}>
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    "& > div": {
                      width: "100%",
                      maxWidth: "100%",
                      boxShadow: "none",
                      borderRadius: 0,
                      padding: 0,
                    },
                  }}
                >
                  <PharmacyMember
                    key={opportunitiesReloadKey}
                    setDispositionOpportunity={setDispositionOpportunity}
                    pharmacyId={memberId}
                    type="pharmacy"
                    onQuickView={handleQuickView}
                    setMemberOptions={setMemberOptions}
                    setMemberDrugMap={setMemberDrugMap}
                    memberId={memberId}
                    title={dispositionTitle ?? "Add Disposition"}
                    onDeleteSelectedRef={deleteSelectedRef}
                    onSelectedCountChange={setSelectedOpportunityCount}
                  />
                </Box>
              </Grid>
            )}

            <AddNoteModal
              open={isNoteModalOpen}
              onClose={() => {
                setIsNoteModalOpen(false);
                setEditingNote(null);
              }}
              programOptions={programMap}
              onSave={handleAddNote}
              hasPinnedNote={notes.some((note) => note.pinNote)}
              canDelete={true}
              loading={notesLoading}
              initialData={
                editingNote
                  ? {
                      noteTypes: [editingNote.type],
                      noteDetails: editingNote.content,
                      note: editingNote.note,
                      pinNote: editingNote.pinNote || false,
                      editedAt: editingNote.editedAt || null,
                      rating: editingNote.rating || null,
                      noteEntry: editingNote.noteEntry || null,
                      programName: editingNote.programName || null,
                      isArchived: editingNote.status === "ARCHIVED",
                    }
                  : null
              }
            />
            <OutreachDispositionModal
              open={isDispositionModalOpen}
              onClose={() => setIsDispositionModalOpen(false)}
              onSave={handleAddDisposition}
              dispositionOpportunity={dispositionOpportunity}
              memberOptions={memberOptions}
              memberDrugMap={memberDrugMap}
              title={dispositionTitle ?? "Add Disposition"}
              memberId={memberId}
              showMonitoringReason={fromMemberMonitoring}
              memberBadgeValue={location?.state?.memberBadgeValue}
              type="pharmacy"
            />
          </Container>
        </ContentContainer>
      </div>

      {quickViewMember && selectedMember && (
        <MemberQuickview
          onClose={handleCloseQuickView}
          source={
            selectedMember?.navigationSource?.badgeType
              ?.toLowerCase()
              .includes("member monitoring") ||
            selectedMember?.navigationSource?.badgeType
              ?.toLowerCase()
              .includes("graduation completed")
              ? "memberMonitoring"
              : "priorityOutreach"
          }
          memberData={{
            ...selectedMember,
            name:
              selectedMember?.pharmacy?.name || selectedMember?.member?.name,
            memberId:
              selectedMember?.member?.memberId || selectedMember?.memberId,
            personCode: `${
              selectedMember?.member?.memberId || selectedMember?.memberId
            }-Subscriber/Policy Holder`,
            status: selectedMember?.recentActivity?.status || "",
            gender: selectedMember?.member?.gender || "",
            dateOfBirth: selectedMember?.member?.dob || "",
            allergies: selectedMember?.member?.knownAllergies || ["None"],
            contact: {
              phone: selectedMember?.provider?.contact || "",
              email:
                selectedMember?.member?.email &&
                selectedMember?.member?.email.trim() !== ""
                  ? selectedMember.member.email
                  : "N/A",
              language: selectedMember?.member?.language || "N/A",
              timeZone: selectedMember?.member?.timezone || "N/A",
              address: "123 Main St.\nAnytown, NY 12345",
            },
            notes: Array.isArray(selectedMember?.notes)
              ? selectedMember.notes
              : [],
            locations: selectedMember?.locations || [],
            pharmacyName: selectedMember?.pharmacyName || "",
            nabp: selectedMember?.pharmacy?.contact || "",
            dea: "FJ1234567",
            memberBadge:
              selectedMember?.memberBadge ||
              selectedMember?.navigationSource?.badgeType ||
              "",
          }}
          onNoteUpdate={(updatedNotes) => {
            const mid =
              selectedMember?.member?.memberId || selectedMember?.memberId;
            if (mid) {
              localStorage.setItem(
                `member_${mid}_notes`,
                JSON.stringify(updatedNotes),
              );
            }
          }}
        />
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
};

MemberDetailsPharmacy.propTypes = {
  pharmacyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedMemberId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  memberId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  providerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default MemberDetailsPharmacy;
